import apiAccessStyle from './api-access.module.css';

import { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSortableTable } from '../../../hooks/useSortableTable';
import useStickyHeader from '../../../hooks/useStickyHeader';

// Util
import { setSortingHeader } from '../../../utils/sorting.util';

// Components
import Checkbox from '../../../components/Checkbox';
import LinkerWithA from '../../../components/LinkerWithA';
import EditableNameBox from '../../../components/EditableNameBox';

// Table field config
const columns = [
  {
    label: '1a1d8746f6',
    accessor: 'name', // API key name
    sortable: true,
    sortbyOrder: 'asc',
  },
  {
    label: 'e27909cf84',
    accessor: 'key_signature', // API key
    sortable: true,
  },
  {
    label: '6078ad6ba2',
    accessor: 'lastAccessIpAddress', // Last access IP address
    sortable: true,
  },
  {
    label: '8a5b1246d8',
    accessor: 'lastAccessLocation', // Last access location
    sortable: true,
  },
  {
    label: '7881ac8986',
    accessor: 'updatedAt', // Last access date / time
    sortable: true,
  },
  {
    label: 'ec53a8c4f0',
    accessor: 'status', // Status
    sortable: true,
  },
  {
    label: 'f886fc6981',
    accessor: 'created_by', // Created by
    sortable: true,
  },
  {
    label: 'dbcdb5d27d',
    accessor: 'createdAt', // Created at
    sortable: true,
  },
];

const ApiAccessTable = ({
  userApiKeysData,
  currentPage,
  pageLimit,
  isCheckedAll,
  onCheckedAll,
  onTableChecked,
  onEnterApiKeyNameValue,
  onResetApiAccessData,
}) => {
  const { t } = useTranslation();

  const { tableRef, stickyStyle } = useStickyHeader();

  const [tableData, handleSorting] = useSortableTable(userApiKeysData, columns);
  const [sortField, setSortField] = useState('');
  const [order, setOrder] = useState('asc');
  const [isCopyUserApiKeysData, setCopyUserApiKeysData] = useState([]);

  useEffect(() => {
    // Reset user edited value when the user changes the current page or page limit.
    setCopyUserApiKeysData([]);
  }, [currentPage, pageLimit, sortField, order]);

  useEffect(() => {
    // Reset table info when the user clicks the table header sort function.
    onResetApiAccessData();
  }, [sortField, order]);

  const handleClickCancelIcon = cancelItem => {
    const originalData = isCopyUserApiKeysData.map(item => {
      if (item.id === cancelItem.id) {
        return { ...item, name: cancelItem.name };
      } else {
        return item;
      }
    });
    setCopyUserApiKeysData(originalData);
  };

  const handleEnterInput = item => {
    const tmpArray = isCopyUserApiKeysData;
    // Use the default data when the user hasn't changed input.
    let newApiKeyName = item;
    tmpArray.forEach(data => {
      if (data.id === item.id) {
        newApiKeyName = { ...item, name: data.name };
      }
    });
    // Set a new input value when the user clicks enter.
    onEnterApiKeyNameValue(newApiKeyName);
  };

  const handleChangeInput = (newApiKeyName, tmpData) => {
    let tmpArray = [];
    if (isCopyUserApiKeysData.length > 0) {
      // User has edited the input value.
      tmpArray = isCopyUserApiKeysData;
    } else {
      tmpArray = userApiKeysData;
    }
    const tmpUserApiKeysData = tmpArray.map(item => {
      if (item.id === tmpData.id) {
        return { ...item, name: newApiKeyName };
      } else {
        return item;
      }
    });
    setCopyUserApiKeysData(tmpUserApiKeysData);
  };

  const checkApiKeyNameValue = inputId => {
    let isApiKeyName = null;
    if (isCopyUserApiKeysData.length > 0) {
      // UI should show the new input value when the user edits the input value.
      const tmpArray = isCopyUserApiKeysData;
      tmpArray.forEach(data => {
        if (data.id === inputId) {
          isApiKeyName = data.name;
        }
      });
    } else {
      const tmpArray = userApiKeysData;
      tmpArray.forEach(data => {
        if (data.id === inputId) {
          isApiKeyName = data.name;
        }
      });
    }
    return isApiKeyName;
  };

  const handleSortingChange = (e, accessor) => {
    const sortOrder =
      accessor === sortField && order === 'desc' ? 'asc' : 'desc';
    setSortField(accessor);
    setOrder(sortOrder);
    handleSorting(accessor, sortOrder);
    setSortingHeader(e);
  };

  return (
    <>
      <Table responsive striped className="table-container" ref={tableRef}>
        <thead style={stickyStyle}>
          <tr>
            <th>
              <Checkbox
                id="rl-th-cb"
                onChange={e => onCheckedAll(e.target.checked)}
                checked={isCheckedAll}
              />
            </th>
            <th>#</th>
            {columns.map(({ label, accessor, sortable }) => {
              const cl = sortable
                ? sortField === accessor && order === 'asc'
                  ? 'is-asc'
                  : sortField === accessor && order === 'desc'
                    ? 'is-desc'
                    : 'default'
                : '';
              return (
                <th key={accessor}>
                  <LinkerWithA
                    label={t(label)}
                    className={`text-decoration-none ${cl}`}
                    key={accessor}
                    onClick={
                      sortable ? e => handleSortingChange(e, accessor) : null
                    }
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {tableData && tableData.length > 0 ? (
            tableData
              .slice(
                (currentPage - 1) * pageLimit,
                (currentPage - 1) * pageLimit + pageLimit
              )
              .map((item, index) => {
                return (
                  <tr key={index}>
                    <td>
                      <Checkbox
                        id={`rl-cb${index}`}
                        onChange={e =>
                          onTableChecked({
                            ...item,
                            checked: e.target.checked,
                          })
                        }
                        checked={item.checked}
                        disabled={item.status === 'INVALID'}
                      />
                    </td>
                    <td>{(currentPage - 1) * pageLimit + index + 1}</td>
                    <td>
                      {item.status === 'INVALID' ? (
                        <span className={apiAccessStyle['span-api-key-name']}>
                          {item.name || ''}
                        </span>
                      ) : (
                        <EditableNameBox
                          key={item.id}
                          onClickCancelIcon={() => handleClickCancelIcon(item)}
                          inputFieldOnKeyDown={() => handleEnterInput(item)}
                          inputFieldOnChange={e =>
                            handleChangeInput(e.target.value, item)
                          }
                          minLength={1}
                          maxLength={64}
                          value={checkApiKeyNameValue(item.id)}
                        />
                      )}
                    </td>
                    <td>{item.key_signature}</td>
                    <td>{item.lastAccessIpAddress}</td>
                    <td>{item.lastAccessLocation}</td>
                    <td>{item.updatedAt}</td>
                    <td>{item.status}</td>
                    <td>{item.created_by}</td>
                    <td>{item.createdAt}</td>
                  </tr>
                );
              })
          ) : (
            <tr>
              <td colSpan="10" className="no-data">
                {t('ad01242a61')}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );
};

export default ApiAccessTable;
